import { DeleteOutline, Edit } from '@mui/icons-material';
import { IconButton, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { _ } from '/@/locale';

type NamedUser =
    | {
          name?: string | null;
          user?: { firstName?: string | null; lastName?: string | null } | null;
      }
    | null
    | undefined;

/**
 * Builds a display name from a user-node-like object: prefer its `name`, otherwise join
 * the user's first + last name. Returns `''` when nothing is available (so callers can
 * fall back to a generic "Registrado" label).
 */
export const getUserDisplayName = (source: NamedUser): string => {
    if (!source) return '';
    if (source.name) return source.name;
    return [source.user?.firstName, source.user?.lastName].filter(Boolean).join(' ').trim();
};

/** A card's footer caption with optional actions right-aligned on the same line. */
export const RegistryFooterRow = ({ children, actions }: { children: ReactNode; actions?: ReactNode }) => (
    <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={1}>
        {children}
        {actions && (
            <Stack direction='row' alignItems='center'>
                {actions}
            </Stack>
        )}
    </Stack>
);

/** "Registrado por {author} - {date}" caption shared by the registry cards. */
export const RegistryFooter = ({
    authorName,
    date,
    actions,
}: {
    authorName?: string;
    date: string;
    /** Mobile-only edit/delete icons (`RegistryFooterActions`); desktop uses the card's ⋮ menu. */
    actions?: ReactNode;
}) => (
    <RegistryFooterRow actions={actions}>
        <Typography variant='caption' color='primary.mui.inactive'>
            {authorName ? `${_('Registrado por')} ${authorName} - ${date}` : `${_('Registrado')} - ${date}`}
        </Typography>
    </RegistryFooterRow>
);

/**
 * Small edit / delete icons for an expanded card's footer on mobile, where the header has no
 * room for a ⋮ menu next to the expand chevron. Same icons as the notes inside the cards.
 */
export const RegistryFooterActions = ({
    onEdit,
    onDelete,
    editDisabled = false,
    deleteDisabled = false,
}: {
    onEdit?: () => void;
    onDelete?: () => void;
    editDisabled?: boolean;
    deleteDisabled?: boolean;
}) => (
    <>
        {onEdit && (
            <IconButton size='small' aria-label={_('Editar')} onClick={onEdit} disabled={editDisabled}>
                <Edit fontSize='small' />
            </IconButton>
        )}
        {onDelete && (
            <IconButton size='small' aria-label={_('Eliminar')} onClick={onDelete} disabled={deleteDisabled}>
                <DeleteOutline fontSize='small' />
            </IconButton>
        )}
    </>
);
